import { useEffect, Fragment } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { setSeo } from "@/utils/seo";
import { Navbar, Footer } from "@/components/Layout";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FLOW = ["Dubbi", "Confronto", "Scelta più consapevole"];

const PRINCIPLES = [
  {
    title: "Prima capire",
    text: "Nessuna scelta dovrebbe partire dalla fretta o dalla pressione. Prima è importante comprendere materiali, tecniche, costi e manutenzione.",
    src: "https://static.prod-images.emergentagent.com/jobs/f24c2250-9a43-4fd4-83d8-5e2d62e8428b/images/2bc5a78f786a6979fecdb537f43cc799b56a687a4a5e09966b78761e07228783.jpeg",
    alt: "Illustrazione di un momento di riflessione prima di una scelta",
  },
  {
    title: "Confrontare con criterio",
    text: "Ogni sistema ha caratteristiche, vantaggi e limiti diversi. Mettere a confronto le possibilità aiuta a evitare decisioni basate soltanto sulla pubblicità o sull'opinione di qualcun altro.",
    src: "https://static.prod-images.emergentagent.com/jobs/f24c2250-9a43-4fd4-83d8-5e2d62e8428b/images/71ad0d4fe6f3156a94f5882b1b6ec30f4d66a799eb2fefc17d98635a747836f9.jpeg",
    alt: "Illustrazione del confronto tra soluzioni e possibilità",
  },
  {
    title: "Scegliere con consapevolezza",
    text: "Il nostro obiettivo è aiutarti a valutare ciò che può essere più adatto alle tue esigenze, senza spingerti verso un prodotto specifico.",
    src: "https://static.prod-images.emergentagent.com/jobs/f24c2250-9a43-4fd4-83d8-5e2d62e8428b/images/8357e065d2deeb9203e282e081ebd3c9f265049abbf8bfc04497757c14328cf2.jpeg",
    alt: "Illustrazione di una scelta fatta con serenità e consapevolezza",
  },
];

const FAQ = [
  {
    q: "HairHelp vende protesi?",
    a: "No. HairHelp non vende direttamente protesi. Offriamo orientamento, informazioni pratiche e consigli per aiutarti a comprendere meglio sistemi, materiali e manutenzione.",
  },
  {
    q: "A chi vi rivolgete?",
    a: "Agli uomini e alle donne che utilizzano già una protesi capillare o stanno pensando di iniziare. Possiamo essere utili anche a chi vuole capire meglio basi, materiali, manutenzione e differenze tra i sistemi.",
  },
  {
    q: "Posso chiedere consiglio anche se sto iniziando?",
    a: "Sì. Il servizio è pensato sia per chi utilizza già una protesi sia per chi si trova ancora nella fase di valutazione e non sa da dove partire.",
  },
  {
    q: "Devo inviare una fotografia?",
    a: "No. Non è necessario inviare fotografie. Puoi descrivere la tua domanda e fornire soltanto le informazioni che ritieni utili.",
  },
  {
    q: "Il consiglio è davvero gratuito?",
    a: "Sì. Il servizio di richiesta del consiglio è gratuito, non comporta obblighi e non richiede l'acquisto di prodotti.",
  },
  {
    q: "Date diagnosi mediche?",
    a: "No. HairHelp non effettua diagnosi mediche e non sostituisce il parere di un medico o di un professionista sanitario.",
  },
];

const sectionH2 = "font-editorial text-[1.85rem] font-semibold leading-tight text-charcoal md:text-[2.35rem]";

export default function ChiSiamo() {
  useEffect(() => {
    setSeo(
      "Chi siamo | HairHelp, esperienza reale sui sistemi capillari",
      "HairHelp nasce dall'esperienza diretta con i sistemi capillari: orientamento indipendente, non vendiamo protesi. Scopri come ragioniamo e i nostri principi."
    );
  }, []);

  return (
    <div className="min-h-screen bg-ivory font-manrope text-charcoal">
      <Navbar />

      {/* 1. TITOLO E IDENTITÀ */}
      <section className="mx-auto max-w-3xl px-6 pt-14 pb-10 md:pt-20 md:pb-14" data-testid="about-page">
        <span className="font-manrope text-xs font-semibold uppercase tracking-[0.18em] text-terracotta">Chi siamo</span>
        <h1 className="mt-4 font-editorial text-[2.1rem] font-semibold leading-[1.15] text-charcoal sm:text-[2.6rem] lg:text-[2.9rem]" data-testid="about-title">
          Non vendiamo una soluzione. Ti aiutiamo a capire quale strada valutare.
        </h1>
        <p className="mt-7 font-manrope text-base leading-relaxed text-charcoal/80 md:text-lg" data-testid="about-intro">
          HairHelp nasce dall'esperienza diretta di chi utilizza protesi capillari realmente da 10 anni e dalla volontà
          di rendere più semplice un mondo spesso difficile da comprendere.
        </p>
        <p className="mt-4 font-manrope text-base leading-relaxed text-charcoal/70 md:text-lg" data-testid="about-audience">
          Ci rivolgiamo a uomini e donne che utilizzano, o stanno pensando di iniziare a utilizzare, protesi capillari,
          patch cutanee, toupet, hair system, parrucche e altre soluzioni non chirurgiche.
        </p>
      </section>

      {/* VIDEO (introduttivo / emozionale) — già presente */}
      <section className="px-6 pb-14 md:pb-20" data-testid="about-video-section">
        <div className="mx-auto max-w-5xl">
          <div className="overflow-hidden rounded-3xl border border-line bg-black shadow-[0_20px_50px_rgba(37,37,37,0.12)]">
            <iframe
              src="/chisiamo-anim.html"
              title="Animazione introduttiva HairHelp"
              className="aspect-[4/5] w-full border-0 sm:aspect-video"
              scrolling="no"
              loading="lazy"
              data-testid="about-animation"
            />
          </div>
          <p className="mt-3 font-manrope text-[11px] text-charcoal/45">
            Immagine illustrativa dell'approccio HairHelp.
          </p>
        </div>
      </section>

      {/* 2. PERCHÉ NASCE HAIRHELP */}
      <section className="bg-beige px-6 py-16 md:py-20" data-testid="about-why">
        <div className="mx-auto max-w-3xl">
          <h2 className={sectionH2} data-testid="about-why-title">Perché spesso è difficile orientarsi</h2>
          <p className="mt-5 font-manrope text-base leading-relaxed text-charcoal/80 md:text-lg">
            Nel mondo dei sistemi capillari si incontrano molti termini, materiali, tecniche e opinioni diverse. Per chi
            inizia, e anche per chi utilizza già una protesi, può essere difficile capire quali informazioni siano
            davvero utili.
          </p>
          <p className="mt-4 font-manrope text-base leading-relaxed text-charcoal/80 md:text-lg">
            HairHelp nasce per mettere ordine tra queste informazioni e trasformare dubbi complessi in indicazioni più
            semplici e concrete, senza pressioni commerciali e senza indirizzare automaticamente verso un prodotto
            specifico.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="about-flow">
            {FLOW.map((step, i) => (
              <Fragment key={step}>
                <span className="inline-flex items-center rounded-full border border-line bg-ivory px-4 py-2 font-manrope text-sm font-semibold text-charcoal/85">
                  {step}
                </span>
                {i < FLOW.length - 1 && <ArrowRight className="h-4 w-4 shrink-0 text-terracotta" strokeWidth={2.2} />}
              </Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* 3. COME RAGIONIAMO */}
      <section className="px-6 py-16 md:py-20" data-testid="about-reasoning">
        <div className="mx-auto grid max-w-5xl items-center gap-10 md:grid-cols-2">
          <div>
            <h2 className={sectionH2} data-testid="about-reasoning-title">Come ragioniamo</h2>
            <p className="mt-5 font-manrope text-base leading-relaxed text-charcoal/80 md:text-lg">
              Non pensiamo che esista una soluzione uguale per tutti. Una scelta può dipendere dal tipo di base,
              dall'uso quotidiano, dal tempo disponibile per la manutenzione, dall'attività fisica, dal risultato
              desiderato e dal budget.
            </p>
            <p className="mt-4 font-manrope text-base leading-relaxed text-charcoal/80 md:text-lg">
              Per questo preferiamo confrontare possibilità, vantaggi e limiti prima di suggerire una direzione.
              L'obiettivo non è vendere una protesi, ma aiutare ogni persona a capire meglio la propria situazione.
            </p>
          </div>
          <figure className="overflow-hidden rounded-3xl border border-line" data-testid="about-reasoning-visual">
            <img
              src="/about-lab.jpg"
              alt="Banco di lavoro con diversi sistemi capillari e strumenti di analisi e manutenzione"
              className="aspect-[4/3] w-full object-cover"
              loading="lazy"
            />
            <figcaption className="bg-beige px-4 py-2.5 font-manrope text-[11px] leading-relaxed text-charcoal/55">
              Immagine illustrativa dell'approccio HairHelp.
            </figcaption>
          </figure>
        </div>
      </section>

      {/* 4. TRE PRINCIPI VISIVI */}
      <section className="bg-beige px-6 py-16 md:py-20" data-testid="about-principles">
        <div className="mx-auto max-w-6xl">
          <h2 className={`${sectionH2} max-w-2xl`} data-testid="about-principles-title">Tre principi che seguiamo</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {PRINCIPLES.map((p, i) => (
              <div key={p.title} data-testid={`about-principle-${i}`} className="flex flex-col overflow-hidden rounded-2xl border border-line bg-ivory">
                <div className="overflow-hidden">
                  <img src={p.src} alt={p.alt} className="aspect-[4/3] w-full object-cover" loading="lazy" />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <span className="font-manrope text-xs font-semibold uppercase tracking-[0.16em] text-terracotta">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-2 font-editorial text-xl font-semibold text-charcoal">{p.title}</h3>
                  <p className="mt-3 font-manrope text-[15px] leading-relaxed text-charcoal/72">{p.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="px-6 py-16 md:py-20" data-testid="about-faq">
        <div className="mx-auto max-w-3xl">
          <h2 className={sectionH2} data-testid="about-faq-title">Domande frequenti</h2>
          <Accordion type="single" collapsible className="mt-8 w-full">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="border-line">
                <AccordionTrigger className="text-left font-manrope text-base font-semibold text-charcoal hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="font-manrope text-[15px] leading-relaxed text-charcoal/75">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* 7. CTA FINALE */}
      <section className="px-6 pb-20 md:pb-24" data-testid="about-cta-section">
        <div className="mx-auto max-w-4xl rounded-3xl bg-charcoal px-7 py-12 text-center md:px-14 md:py-14">
          <h2 className="font-editorial text-[1.85rem] font-semibold leading-tight text-ivory md:text-[2.35rem]" data-testid="about-cta-title">
            Vuoi capire meglio da dove partire?
          </h2>
          <p className="mx-auto mt-5 max-w-2xl font-manrope text-base leading-relaxed text-ivory/85 md:text-lg">
            Se hai una domanda sulla tua protesi, sui materiali, sulla manutenzione o sui primi passi, puoi inviarci una
            richiesta. Ti risponderemo in modo privato, concreto e senza obblighi.
          </p>
          <Link
            to="/fai-una-domanda"
            data-testid="about-cta"
            className="mt-9 inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-sage px-7 py-3.5 font-manrope text-[15px] font-semibold text-ivory shadow-[0_10px_24px_rgba(63,90,82,0.25)] transition-all duration-300 hover:bg-[#33463f] active:scale-[0.98]"
          >
            Chiedi un consiglio gratuito <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="mt-5">
            <Link to="/" data-testid="about-home-link" className="font-manrope text-sm font-medium text-ivory/60 underline underline-offset-4 transition-colors hover:text-ivory">
              Torna alla homepage
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
